import { useState } from "react";
import { motion } from "motion/react";
import type { SimulatorInput, SimulatorResult, ScoreBreakdown, SensitivityAnalysis } from "./SimulatorEngine";
import { SENSITIVITY_PARAMS, analyzeSensitivity } from "./SimulatorEngine";

function formatKRW(n: number): string {
  if (n < 0) return "-" + Math.abs(n).toLocaleString() + "원";
  return n.toLocaleString() + "원";
}

function formatPercent(n: number): string {
  const sign = n >= 0 ? "+" : "";
  return sign + n.toFixed(1) + "%";
}

function formatShortKRW(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 100000000) return sign + (abs / 100000000).toFixed(1) + "억원";
  if (abs >= 10000) return sign + Math.round(abs / 10000) + "만원";
  return n.toLocaleString() + "원";
}

interface SimulatorResultProps {
  result: SimulatorResult;
  data: SimulatorInput;
}

export default function SimulatorResultView({ result, data }: SimulatorResultProps) {
  const {
    maxBidPrice, loanAmount, expectedSalePrice,
    surfaceProfit, surfaceROE, netProfit, actualROE,
    annualizedROE, monthlyAvgProfit,
    costs, breakevenSalePrice, breakevenSaleRate,
    score, grade, gradeLabel, gradeColor, recommendationDetail,
  } = result;

  const gaugeColor = score.total >= 90 ? "#10B981" : score.total >= 80 ? "#F59E0B" : score.total >= 70 ? "#F97316" : "#EF4444";
  const profitGap = surfaceProfit - netProfit;
  const profitGapRatio = surfaceProfit !== 0 ? Math.round((Math.abs(profitGap) / Math.abs(surfaceProfit)) * 100) : 0;
  const roeGap = surfaceROE - actualROE;

  const [sensitivityParam, setSensitivityParam] = useState<string | null>(null);
  const sensitivityData: SensitivityAnalysis | null = sensitivityParam
    ? analyzeSensitivity(data, sensitivityParam as keyof SimulatorInput)
    : null;

  return (
    <div className="space-y-5">
      {/* Title */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#1C1917]">AI 투자 판단 결과</h2>
          <p className="text-xs text-[#8C8780] mt-0.5">매매사업자 관점 정밀 수익률 분석</p>
        </div>
        <span className="px-2.5 py-1 text-xs font-semibold bg-[#C4956A]/10 text-[#C4956A] rounded-full">실시간</span>
      </div>

      {/* ═══════════════════════════════════════════ */}
      {/* SUMMARY CARD: 한눈에 보는 핵심 지표 */}
      {/* ═══════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative overflow-hidden rounded-xl border-2 p-5"
        style={{ borderColor: gradeColor, backgroundColor: gradeColor + "08" }}
      >
        <div className="flex items-start gap-5">
          {/* Grade Badge */}
          <div className="flex-shrink-0 text-center">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-extrabold"
              style={{ backgroundColor: gradeColor }}
            >
              {grade}
            </div>
            <p className="text-[10px] font-bold mt-1.5" style={{ color: gradeColor }}>{gradeLabel.split(" — ")[0]}</p>
          </div>
          {/* Key Metrics */}
          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "AI 점수", value: score.total + "점", color: gaugeColor, sub: "/ 100" },
                { label: "실제 ROE", value: formatPercent(actualROE), color: actualROE >= 0 ? "#059669" : "#DC2626", sub: "연환산 " + formatPercent(annualizedROE) },
                { label: "실제 순이익", value: formatShortKRW(netProfit), color: netProfit >= 0 ? "#059669" : "#DC2626", sub: "월 " + formatShortKRW(monthlyAvgProfit) },
              ].map((metric, i) => (
                <div key={i} className="bg-white/70 rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-[#8C8780] mb-0.5">{metric.label}</p>
                  <p className="text-sm font-extrabold" style={{ color: metric.color }}>{metric.value}</p>
                  <p className="text-[10px] text-[#B8B3AE]">{metric.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════ */}
      {/* SECTION 1: 투자 기본 파라미터 요약 */}
      {/* ═══════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.02, duration: 0.4 }}
        className="bg-white rounded-xl border border-[#E8E5E0] p-5"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">1</span>
          </div>
          <h4 className="text-sm font-bold text-[#1C1917]">투자 기본 파라미터</h4>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
          {[
            { label: "자기자본 (E)", value: formatShortKRW(data.equity) },
            { label: "대출비율 (LTV)", value: (data.ltv * 100).toFixed(0) + "%" },
            { label: "대출금리 (r)", value: data.loanRate.toFixed(1) + "%" },
            { label: "보유예정기간 (m)", value: data.holdingMonths + "개월" },
            { label: "감정가", value: formatShortKRW(data.appraisalValue) },
            { label: "낙찰가율 목표", value: (data.bidRate * 100).toFixed(0) + "%" },
            { label: "예상 매도가율", value: (data.saleRate * 100).toFixed(0) + "%" },
            { label: "물건 종류", value: data.propertyType },
            { label: "최대 낙찰 가능금액", value: formatShortKRW(maxBidPrice), highlight: true },
            { label: "대출금액", value: formatShortKRW(loanAmount) },
            { label: "참조 입찰가", value: formatShortKRW(result.referenceBidPrice) },
            { label: "예상 매도가", value: formatShortKRW(expectedSalePrice) },
          ].map((row, i) => (
            <div key={i} className={`flex justify-between items-center py-1.5 border-b border-[#F5F2EE] last:border-0 ${row.highlight ? "bg-amber-50/50 -mx-2 px-2 rounded" : ""}`}>
              <span className="text-xs text-[#8C8780]">{row.label}</span>
              <span className={`text-xs font-semibold ${row.highlight ? "text-[#C4956A]" : "text-[#1C1917]"}`}>{row.value}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════ */}
      {/* SECTION 2: 비용 정밀 분석 + 워터폴 차트 */}
      {/* ═══════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.4 }}
        className="bg-white rounded-xl border border-[#E8E5E0] p-5"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">2</span>
          </div>
          <h4 className="text-sm font-bold text-[#1C1917]">비용 정밀 분석 + 워터폴 차트</h4>
        </div>

        {/* Waterfall Chart */}
        <div className="mb-5">
          <p className="text-[10px] font-bold text-[#8C8780] uppercase tracking-wide mb-3">표면이익 → 실제순이익 추적</p>
          <div className="space-y-1.5">
            {(() => {
              const maxAbs = Math.max(
                Math.abs(surfaceProfit),
                Math.abs(costs.totalCost),
                Math.abs(netProfit),
                1,
              );
              const barMaxWidth = 100;

              const items = [
                { label: "표면이익", value: surfaceProfit, color: surfaceProfit >= 0 ? "#10B981" : "#EF4444", type: "start" as const },
                { label: "취득비용", value: -costs.acquisitionCost, color: "#F97316", type: "cost" as const },
                { label: "개선비용", value: -costs.improvementCost, color: "#F97316", type: "cost" as const },
                { label: "보유비용(금융)", value: -costs.holdingCost, color: "#F97316", type: "cost" as const },
                { label: "매도비용", value: -costs.sellingCost, color: "#F97316", type: "cost" as const },
                { label: "실제 순이익", value: netProfit, color: netProfit >= 0 ? "#059669" : "#EF4444", type: "end" as const },
              ];

              let cumulative = surfaceProfit;

              return items.map((item, idx) => {
                if (item.type === "start") {
                  cumulative = item.value;
                } else if (item.type === "cost") {
                  cumulative = cumulative + item.value;
                } else {
                  cumulative = netProfit;
                }

                const barWidth = (Math.abs(item.value) / maxAbs) * barMaxWidth;
                const isNegative = item.value < 0;
                const barStyle = item.type === "start" || item.type === "end"
                  ? { width: `${Math.min(barMaxWidth, barWidth)}%` }
                  : { width: `${Math.min(barMaxWidth, barWidth)}%` };

                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-[10px] text-[#8C8780] w-[84px] flex-shrink-0 text-right whitespace-nowrap">
                      {item.label}
                    </span>
                    <div className="flex-1 h-5 bg-[#F0EDE8] rounded-sm relative overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: barStyle.width }}
                        transition={{ delay: 0.3 + idx * 0.05, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
                        className="h-full rounded-sm"
                        style={{ backgroundColor: item.color }}
                      />
                    </div>
                    <span
                      className="text-[10px] font-bold w-[72px] flex-shrink-0 text-right whitespace-nowrap"
                      style={{ color: item.value >= 0 ? "#059669" : item.type === "start" ? (surfaceProfit >= 0 ? "#059669" : "#EF4444") : "#EF4444" }}
                    >
                      {item.value >= 0 ? "+" : ""}{formatShortKRW(item.value)}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Detailed Cost Table */}
        <div className="border-t border-[#F0EDE8] pt-4 space-y-0">
          <p className="text-[10px] font-bold text-[#8C8780] uppercase tracking-wide mb-2">비용 상세 내역</p>
          {[
            {
              label: "취득비용",
              amount: costs.acquisitionCost,
              detail: `취득세 ${(data.acquisitionTaxRate * 100).toFixed(1)}% + 법무·등기비 ${formatShortKRW(data.legalCost)}`,
            },
            {
              label: "개선비용",
              amount: costs.improvementCost,
              detail: `수리 ${formatShortKRW(data.repairCost)} + 명도 ${formatShortKRW(data.evictionCost)} + 청소 ${formatShortKRW(data.cleanCost)}`,
            },
            {
              label: "보유비용 (금융)",
              amount: costs.holdingCost,
              detail: `대출 ${formatShortKRW(loanAmount)} × ${data.loanRate}% × (${data.holdingMonths}/12) → 월 이자 약 ${formatShortKRW(costs.monthlyInterest)}`,
            },
            {
              label: "매도비용",
              amount: costs.sellingCost,
              detail: `중개수수료 ${(data.brokerRate * 100).toFixed(1)}% × 매도가 ${formatShortKRW(expectedSalePrice)}`,
            },
          ].map((row, i) => (
            <div key={i} className="py-2.5 border-b border-[#F5F2EE] last:border-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs font-semibold text-[#1C1917]">{row.label}</span>
                <span className="text-xs font-bold text-rose-500">{formatKRW(row.amount)}</span>
              </div>
              <p className="text-[10px] text-[#B8B3AE]">{row.detail}</p>
            </div>
          ))}
          <div className="flex items-center justify-between py-3 mt-1 border-t-2 border-[#1C1917]">
            <span className="text-sm font-bold text-[#1C1917]">총 부대비용 (C_total)</span>
            <span className="text-sm font-bold text-rose-500">{formatKRW(costs.totalCost)}</span>
          </div>
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════ */}
      {/* SECTION 3: 수익성 비교 + 손익분기점 */}
      {/* ═══════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        className="bg-white rounded-xl border border-[#E8E5E0] p-5"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">3</span>
          </div>
          <h4 className="text-sm font-bold text-[#1C1917]">수익성 비교 + 손익분기점</h4>
        </div>

        {/* 착시 경고 */}
        {profitGap > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-3">
            <div className="w-6 h-6 rounded-md bg-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-alert-line text-white text-xs"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-amber-700 mb-0.5">표면이익 착시 경고</p>
              <p className="text-[10px] text-amber-600 leading-relaxed">
                표면이익 {formatShortKRW(surfaceProfit)} 중 <strong>{formatShortKRW(profitGap)}</strong> ({profitGapRatio}%)가 부대비용으로 소멸됩니다.
                실제 순이익은 <strong>{formatShortKRW(netProfit)}</strong>입니다.
              </p>
            </div>
          </div>
        )}

        {/* 표면 vs 실제 비교 */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          {[
            { label: "표면이익", value: formatShortKRW(surfaceProfit), sub: "V_sale - V_bid", bg: "bg-[#FAFAF8]", border: "border-[#E8E5E0]", color: surfaceProfit >= 0 ? "text-[#1C1917]" : "text-rose-500" },
            { label: "실제 순이익", value: formatShortKRW(netProfit), sub: "표면이익 - C_total", bg: netProfit >= 0 ? "bg-emerald-50" : "bg-rose-50", border: netProfit >= 0 ? "border-emerald-200" : "border-rose-200", color: netProfit >= 0 ? "text-emerald-600" : "text-rose-500" },
            { label: "표면 ROE", value: formatPercent(surfaceROE), sub: "표면이익 / E × 100", bg: "bg-[#FAFAF8]", border: "border-[#E8E5E0]", color: surfaceROE >= 0 ? "text-[#1C1917]" : "text-rose-500" },
            { label: "실제 ROE", value: formatPercent(actualROE), sub: "P_net / E × 100", bg: actualROE >= 0 ? "bg-emerald-50" : "bg-rose-50", border: actualROE >= 0 ? "border-emerald-200" : "border-rose-200", color: actualROE >= 0 ? "text-emerald-600" : "text-rose-500" },
          ].map((box, i) => (
            <div key={i} className={`${box.bg} ${box.border} rounded-lg p-3 border`}>
              <p className="text-[10px] text-[#8C8780] mb-0.5">{box.label}</p>
              <p className={`text-base font-bold ${box.color}`}>{box.value}</p>
              <p className="text-[10px] text-[#B8B3AE]">{box.sub}</p>
            </div>
          ))}
        </div>

        {/* ROE 격차 시각화 */}
        {surfaceROE > 0 && (
          <div className="mb-5">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-[#8C8780]">표면 ROE</span>
                <span className="font-semibold text-[#1C1917]">{formatPercent(surfaceROE)}</span>
              </div>
              <div className="h-2.5 bg-[#F0EDE8] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#D1CCC2] rounded-full"
                  style={{ width: `${Math.min(100, Math.max(0, (surfaceROE / Math.max(1, surfaceROE + Math.abs(roeGap))) * 100))}%` }}
                ></div>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-emerald-600 font-semibold">실제 ROE</span>
                <span className="font-bold text-emerald-600">{formatPercent(actualROE)}</span>
              </div>
              <div className="h-2.5 bg-[#F0EDE8] rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, Math.max(0, (actualROE / Math.max(1, surfaceROE)) * 100))}%` }}
                  transition={{ delay: 0.3, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const }}
                  className="h-full bg-emerald-500 rounded-full"
                />
              </div>
            </div>
            {roeGap > 0 && (
              <p className="text-[10px] text-rose-500 text-right mt-1">부대비용으로 {formatPercent(roeGap)}p 증발</p>
            )}
          </div>
        )}

        {/* 손익분기점 */}
        <div className={`rounded-lg p-4 border ${breakevenSaleRate <= data.saleRate * 100 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center gap-2 mb-2">
            <i className={`text-sm ${breakevenSaleRate <= data.saleRate * 100 ? "ri-check-double-line text-emerald-500" : "ri-error-warning-line text-amber-500"}`}></i>
            <h5 className="text-xs font-bold text-[#1C1917]">손익분기점 분석</h5>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] text-[#8C8780]">손익분기 매도가</p>
              <p className="text-sm font-bold text-[#1C1917]">{formatShortKRW(breakevenSalePrice)}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#8C8780]">손익분기 매도가율</p>
              <p className="text-sm font-bold text-[#1C1917]">{breakevenSaleRate.toFixed(1)}%</p>
            </div>
            <div className="col-span-2">
              <p className="text-[10px] text-[#8C8780]">현재 매도가 대비</p>
              <p className={`text-xs font-semibold ${breakevenSaleRate <= data.saleRate * 100 ? "text-emerald-600" : "text-amber-600"}`}>
                {breakevenSaleRate <= data.saleRate * 100
                  ? `손익분기점보다 ${((data.saleRate * 100) - breakevenSaleRate).toFixed(1)}%p 높습니다 — 안전 마진 확보`
                  : `손익분기점보다 ${(breakevenSaleRate - (data.saleRate * 100)).toFixed(1)}%p 낮습니다 — 매도가 조정 필요`}
              </p>
            </div>
          </div>
        </div>

        {/* 추가 지표 */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[
            { label: "연환산 ROE", value: formatPercent(annualizedROE), color: annualizedROE >= 0 ? "text-[#C4956A]" : "text-rose-500" },
            { label: "월평균 순수익", value: formatShortKRW(monthlyAvgProfit), color: monthlyAvgProfit >= 0 ? "text-[#1C1917]" : "text-rose-500" },
            { label: "비용 비율", value: (expectedSalePrice > 0 ? Math.round((costs.totalCost / expectedSalePrice) * 100) : 0) + "%", color: "text-[#8C8780]" },
          ].map((m, i) => (
            <div key={i} className="bg-[#FAFAF8] rounded-lg p-3 text-center">
              <p className="text-[10px] text-[#8C8780] mb-0.5">{m.label}</p>
              <p className={`text-sm font-bold ${m.color}`}>{m.value}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════ */}
      {/* SECTION 4: AI 종합 투자 점수 및 판정 */}
      {/* ═══════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.4 }}
        className="bg-white rounded-xl border border-[#E8E5E0] p-5"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">4</span>
          </div>
          <h4 className="text-sm font-bold text-[#1C1917]">AI 종합 투자 점수 및 최종 판정</h4>
        </div>

        <div className="flex items-center gap-5 mb-5">
          {/* Circular Gauge */}
          <div className="relative flex-shrink-0">
            <svg width="100" height="100" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#F0EDE8" strokeWidth="7" />
              <circle
                cx="50" cy="50" r="42" fill="none"
                stroke={gaugeColor}
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray={`${(score.total / 100) * 211.1} 211.1`}
                transform="rotate(-90 50 50)"
                style={{ transition: "stroke-dasharray 0.6s ease" }}
              />
              <text x="50" y="48" textAnchor="middle" fontSize="24" fontWeight="bold" fill="#1C1917" fontFamily="'Noto Sans KR', sans-serif">
                {score.total}
              </text>
              <text x="50" y="63" textAnchor="middle" fontSize="8" fontWeight="500" fill="#8C8780" fontFamily="'Noto Sans KR', sans-serif">
                / 100점
              </text>
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-2"
              style={{ backgroundColor: gradeColor + "15", color: gradeColor }}
            >
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: gradeColor }}
              >
                {grade}
              </span>
              <span className="text-sm font-bold">{gradeLabel}</span>
            </div>
            <p className="text-xs text-[#5C5650] leading-relaxed">{recommendationDetail}</p>
          </div>
        </div>

        {/* 점수 항목별 상세 */}
        <div className="space-y-3 border-t border-[#F0EDE8] pt-4">
          <p className="text-[10px] font-bold text-[#8C8780] uppercase tracking-wide">점수 항목별 분석</p>
          {([
            { key: "bidDiscount" as keyof ScoreBreakdown, label: "낙찰 할인율", max: 30, icon: "ri-discount-percent-line", desc: "감정가 대비 할인 폭 (" + score.discountRate + "%)" },
            { key: "roeScore" as keyof ScoreBreakdown, label: "실제 ROE", max: 25, icon: "ri-funds-line", desc: "ROE 80% 이상 만점" },
            { key: "liquidity" as keyof ScoreBreakdown, label: "유동성/거래량", max: 20, icon: "ri-exchange-line", desc: "물건종류 + 최근 거래건수" },
            { key: "cashFlow" as keyof ScoreBreakdown, label: "현금흐름/DSCR", max: 15, icon: "ri-money-dollar-circle-line", desc: "월 이자 대비 이익 여유도" },
            { key: "eviction" as keyof ScoreBreakdown, label: "명도 난이도", max: 10, icon: "ri-home-smile-line", desc: "점유자: " + data.occupantType },
          ]).map((item) => {
            const value = score[item.key];
            const pct = (value / item.max) * 100;
            let barColor = "#10B981";
            if (pct < 40) barColor = "#EF4444";
            else if (pct < 60) barColor = "#F97316";
            else if (pct < 80) barColor = "#F59E0B";
            return (
              <div key={item.key}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-xs font-medium text-[#5C5650] flex items-center gap-1.5">
                      <i className={`${item.icon} text-[#B8B3AE]`}></i>
                      {item.label}
                    </span>
                    <p className="text-[10px] text-[#B8B3AE] ml-5">{item.desc}</p>
                  </div>
                  <span className="text-xs font-bold text-[#1C1917] whitespace-nowrap">
                    {value} <span className="text-[#B8B3AE] font-normal">/ {item.max}점</span>
                  </span>
                </div>
                <div className="h-1.5 bg-[#F0EDE8] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: barColor }}
                  />
                </div>
              </div>
            );
          })}
          <div className="flex items-center justify-between pt-2 border-t border-[#F0EDE8]">
            <span className="text-sm font-bold text-[#1C1917]">AI 종합 점수</span>
            <span className="text-sm font-bold" style={{ color: gaugeColor }}>{score.total}점 / 100점</span>
          </div>
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════ */}
      {/* SECTION 5: 민감도 분석 */}
      {/* ═══════════════════════════════════════════ */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18, duration: 0.4 }}
        className="bg-white rounded-xl border border-[#E8E5E0] p-5"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-[#1C1917] flex items-center justify-center">
            <span className="text-[10px] text-white font-bold">5</span>
          </div>
          <h4 className="text-sm font-bold text-[#1C1917]">민감도 분석 — 변수 변화에 따른 등급 추적</h4>
        </div>
        <p className="text-[10px] text-[#B8B3AE] mb-3">아래 파라미터 중 하나를 선택하면 ±범위에서 ROE·등급 변화를 추적합니다.</p>

        {/* Param selector pills */}
        <div className="flex gap-1.5 flex-wrap mb-4">
          {SENSITIVITY_PARAMS.map((p) => {
            const isActive = sensitivityParam === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setSensitivityParam(isActive ? null : p.key)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? "bg-[#C4956A] text-white border-[#C4956A] font-semibold"
                    : "bg-white text-[#5C5650] border-[#E8E5E0] hover:border-[#C4956A]"
                }`}
              >
                <i className={`${p.icon} mr-1`}></i>
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Sensitivity chart */}
        {sensitivityData && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="space-y-2"
          >
            <div className="flex items-center h-[120px] gap-1 items-end">
              {sensitivityData.points.map((pt, idx) => {
                const roeVal = pt.result.actualROE;
                const maxRoe = Math.max(...sensitivityData.points.map((p) => Math.abs(p.result.actualROE)), 1);
                const barH = Math.max(4, (Math.abs(roeVal) / maxRoe) * 110);
                const isCurrent = idx === Math.floor(sensitivityData.points.length / 2);
                let color = "#10B981";
                if (pt.result.grade === "A") color = "#F59E0B";
                else if (pt.result.grade === "B") color = "#F97316";
                else if (pt.result.grade === "C") color = "#EF4444";
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                    <span className="text-[9px] font-bold" style={{ color }}>{pt.result.grade}</span>
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: barH }}
                      transition={{ delay: 0.3 + idx * 0.03, duration: 0.4 }}
                      className="w-full rounded-t-sm"
                      style={{
                        backgroundColor: color,
                        maxWidth: 24,
                        opacity: isCurrent ? 1 : 0.55,
                        border: isCurrent ? "2px solid #1C1917" : "none",
                      }}
                    />
                    <span className={`text-[8px] text-[#B8B3AE] ${isCurrent ? "font-bold text-[#1C1917]" : ""} truncate w-full text-center`}>
                      {pt.paramLabel}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Legend */}
            <div className="flex items-center gap-3 text-[9px] pt-2 border-t border-[#F0EDE8]">
              <span className="text-[#8C8780]">{sensitivityData.unit} 기준 ±범위</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#10B981]"></span>S</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#F59E0B]"></span>A</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#F97316]"></span>B</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#EF4444]"></span>C</span>
              <span className="text-[#B8B3AE]">| 굵은 테두리 = 현재값</span>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* 등급별 액션 가이드 */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className={`rounded-xl p-4 ${
          grade === 'S' ? 'bg-emerald-50 border border-emerald-200' :
          grade === 'A' ? 'bg-amber-50 border border-amber-200' :
          grade === 'B' ? 'bg-orange-50 border border-orange-200' :
          'bg-rose-50 border border-rose-200'
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
            style={{ backgroundColor: gradeColor }}
          >
            {grade}
          </div>
          <span className="text-xs font-bold" style={{ color: gradeColor }}>최종 의사결정</span>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: gradeColor === '#10B981' ? '#065F46' : gradeColor === '#F59E0B' ? '#78350F' : gradeColor === '#F97316' ? '#7C2D12' : '#7F1D1D' }}>
          {grade === 'S' && '모든 지표가 우수합니다. 목표 낙찰가를 상향 조정해도 충분한 수익 여력이 있으므로 적극적으로 입찰에 참여하세요. 경쟁률이 높을 수 있으니 사전 자금 준비를 철저히 하시기 바랍니다.'}
          {grade === 'A' && '안정적인 투자처입니다. 기준 입찰가를 준수하여 입찰에 참여하세요. 무리한 상향은 수익률을 급격히 낮출 수 있으니 주의하시기 바랍니다.'}
          {grade === 'B' && '수익 여력이 제한적입니다. 보수적으로 입찰가를 하향 조정하거나, 비용 절감 방안을 먼저 검토하세요. 추가 실사 후 신중히 판단하시기 바랍니다.'}
          {grade === 'C' && '현재 조건으로는 투자 가치가 매우 낮습니다. 비용 구조 재검토 또는 더 나은 물건 탐색을 권장합니다. 이 물건은 포기하는 것이 합리적인 판단입니다.'}
        </p>
      </motion.div>

      {/* 산출 공식 레퍼런스 */}
      <div className="bg-[#FAFAF8] rounded-xl border border-[#E8E5E0] p-4">
        <h4 className="text-[10px] font-bold text-[#8C8780] uppercase tracking-wide mb-2">산출 공식</h4>
        <div className="text-[10px] text-[#5C5650] leading-relaxed space-y-1 font-mono">
          <p><strong>1.</strong> V_bid = E / (1-L) | 대출금 = V_bid × L</p>
          <p><strong>2.</strong> V_sale = V_app × R_sale | 표면이익 = V_sale - V_bid</p>
          <p><strong>3.</strong> C_total = 취득 + 개선 + 보유 + 매도</p>
          <p><strong>4.</strong> P_net = 표면이익 - C_total | ROE = P_net / E × 100%</p>
          <p><strong>5.</strong> 연환산ROE = ROE × (12/m) | 손익분기 = (V_bid+고정비)/(1-R_broker)</p>
          <p><strong>6.</strong> AI 점수 = Σ(5항목 × 가중치) | S≥90/A≥80/B≥70/C 그외</p>
        </div>
      </div>
    </div>
  );
}